/* Helper to initialize Google Identity Services with origin checks */
/* global google */

/**
 * Wait for Google Identity Services library to load
 */
function waitForGoogleLibrary(maxAttempts = 50, interval = 100) {
  return new Promise((resolve, reject) => {
    let attempts = 0;
    const checkLibrary = () => {
      if (window.google && window.google.accounts && window.google.accounts.id) {
        resolve(true);
      } else if (attempts >= maxAttempts) {
        reject(new Error('Google Identity library failed to load after maximum attempts'));
      } else {
        attempts++;
        setTimeout(checkLibrary, interval);
      }
    };
    checkLibrary();
  });
}

/**
 * Initialize Google Sign-In with proper error handling and retry logic
 */
export function initGoogleSignIn(callback) {
  const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
  const allowed = (process.env.  REACT_APP_ALLOWED_ORIGINS || '')
    .split(',')
    .map(s => s.trim())
    .filter(Boolean);
  const origin = window.location.origin;

  if (!clientId) {
    console.error('Google Sign-In not initialized: REACT_APP_GOOGLE_CLIENT_ID is not set.');
    return false;
  }

  if (allowed.length && !allowed.includes(origin)) {
    console.error(`Google Sign-In not initialized: origin "${origin}" is not in REACT_APP_ALLOWED_ORIGINS (${allowed.join(', ')}).`);
    return false;
  }

  // Wait for Google library to load, then initialize
  waitForGoogleLibrary()
    .then(() => {
      try {
        // Log configuration for debugging (helpful if 403 errors occur)
        // Note: If you see 403 errors, ensure this origin is added to Google Cloud Console
        // → APIs & Services → Credentials → OAuth Client → Authorized JavaScript origins
        console.info('🔐 Google Sign-In Configuration:', {
          origin: origin,
          clientId: clientId.substring(0, 20) + '...',
          fullUrl: window.location.href
        });
        
        // Initialize Google Sign-In
        google.accounts.id.initialize({ 
          client_id: clientId, 
          callback: callback,
          auto_select: false,
          cancel_on_tap_outside: true
        });

        // Wait for DOM element to be available, then render button
        const renderButton = () => {
          const btn = document.getElementById('googleSignInDiv');
          if (btn) {
            try {
              google.accounts.id.renderButton(btn, { 
                theme: 'outline', 
                size: 'large',
                text: 'signin_with',
                shape: 'rectangular',
                logo_alignment: 'left'
              });
              console.info('✅ Google Sign-In initialized and button rendered successfully.');
            } catch (renderError) {
              console.error('Error rendering Google Sign-In button:', renderError);
            }
          } else {
            // Retry if element not found (might be React still mounting)
            setTimeout(renderButton, 100);
          }
        };

        // Try to render immediately, or wait a bit for React to mount
        if (document.getElementById('googleSignInDiv')) {
          renderButton();
        } else {
          setTimeout(renderButton, 200);
        }
      } catch (error) {
        console.error('Google Sign-In initialization error:', error);
      }
    })
    .catch((error) => {
      console.error('❌ Failed to load Google Identity library:', error);
      console.warn('💡 Make sure the Google Identity Services script is loaded in index.html');
      console.warn('💡 Also verify your origin is added to Google Cloud Console → Authorized JavaScript origins');
    });

  return true; // Return true to indicate initialization attempt started
}
